import { importPlaceSchema } from "@/utils/validators/places.schema";
import { PLACES_CATEGORIES } from "@/utilities/constants/PlacesCategories";

export interface CSVPlaceData {
  name: string;
  category?: string;
  latitude: string;
  longitude: string;
}

export interface ValidationError {
  row: number;
  field: string;
  message: string;
  value: string;
}

export interface CSVValidationResult {
  isValid: boolean;
  errors: ValidationError[];
  validData: CSVPlaceData[];
  totalRows: number;
}

export function validateCSVData(csvData: CSVPlaceData[]): CSVValidationResult {
  const errors: ValidationError[] = [];
  const validData: CSVPlaceData[] = [];

  csvData.forEach((row, index) => {
    const rowNumber = index + 1; // Adding 1 because CSV rows are 1-indexed for users

    try {
      // Validate using Zod schema
      const result = importPlaceSchema.safeParse(row);

      if (result.success) {
        validData.push(row);
      } else {
        // Extract validation errors
        result.error.errors.forEach((error) => {
          errors.push({
            row: rowNumber,
            field: error.path.join('.'),
            message: error.message,
            value: String(row[error.path[0] as keyof CSVPlaceData] || '')
          });
        });
      }
    } catch (error) {
      errors.push({
        row: rowNumber,
        field: 'general',
        message: 'Unexpected error during validation',
        value: JSON.stringify(row)
      });
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
    validData,
    totalRows: csvData.length
  };
}

export function parseCSV(csvText: string): CSVPlaceData[] {
  const lines = csvText.split('\n').filter(line => line.trim());

  if (lines.length < 2) {
    throw new Error('CSV must contain at least a header row and one data row');
  }

  // Extract headers
  const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, '').toLowerCase());

  // Validate required headers
  const requiredHeaders = ['name', 'latitude', 'longitude'];
  const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));

  if (missingHeaders.length > 0) {
    throw new Error(`Missing required headers: ${missingHeaders.join(', ')}`);
  }

  // Parse data rows
  const data: CSVPlaceData[] = [];

  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(',').map(v => v.trim().replace(/^"|"$/g, ''));

    const rowData: CSVPlaceData = {
      name: values[headers.indexOf('name')] || '',
      category: values[headers.indexOf('category')] || undefined,
      latitude: values[headers.indexOf('latitude')] || '',
      longitude: values[headers.indexOf('longitude')] || ''
    };

    data.push(rowData);
  }

  return data;
}

export function generateValidationErrorReport(errors: ValidationError[]): string {
  const errorByField = errors.reduce((acc, error) => {
    if (!acc[error.field]) {
      acc[error.field] = [];
    }
    acc[error.field].push(error);
    return acc;
  }, {} as Record<string, ValidationError[]>);

  let report = 'CSV Validation Error Report:\n\n';

  Object.entries(errorByField).forEach(([field, fieldErrors]) => {
    report += `${field.toUpperCase()} Errors (${fieldErrors.length}):\n`;

    fieldErrors.slice(0, 5).forEach(error => { // Show first 5 errors per field
      report += `  Row ${error.row}: ${error.message}\n`;
      if (error.value) {
        report += `    Value: "${error.value}"\n`;
      }
    });

    if (fieldErrors.length > 5) {
      report += `  ... and ${fieldErrors.length - 5} more ${field} errors\n`;
    }
    report += '\n';
  });

  return report;
}

export function getAvailableCategories(): string[] {
  return [...PLACES_CATEGORIES].sort();
}